import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button, Card, Form, Input, Modal, Popconfirm, Result, Space, Switch, Table, Tag, message } from 'antd'
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import axios from 'axios'
import { AppContext } from 'app/AppContext'
import { MOVIMIENTOS_USUARIOS } from 'app_permissions/dashboard'
import { userHasPermission } from 'utils/userHasPermission'

const url = `${process.env.REACT_APP_BASE_URL_MOVEMENTS}/manual-movement-reasons`

export default function MovimientosManuales() {
  const { rolData } = useContext(AppContext)
  const canView = userHasPermission(rolData?.role, MOVIMIENTOS_USUARIOS.VISUALIZAR)
  const canCreate = userHasPermission(rolData?.role, MOVIMIENTOS_USUARIOS.CREAR)
  const canEdit = userHasPermission(rolData?.role, MOVIMIENTOS_USUARIOS.EDITAR)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [includeInactive, setIncludeInactive] = useState(true)
  const [editor, setEditor] = useState(null)
  const [form] = Form.useForm()
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(url, { params: { include_inactive: includeInactive } })
      setRows(data)
    } catch (error) {
      message.error(error?.response?.data?.message || 'No fue posible cargar los motivos')
    } finally { setLoading(false) }
  }, [includeInactive])
  useEffect(() => { if (canView) load() }, [canView, load])
  const open = (record = {}) => { setEditor(record); form.setFieldsValue({ name: record.name || '' }) }
  const save = async () => {
    let values
    try { values = await form.validateFields() } catch { return }
    setSaving(true)
    try {
      if (editor.id) await axios.patch(`${url}/${editor.id}`, values)
      else await axios.post(url, values)
      setEditor(null)
      message.success('Motivo guardado')
      await load()
    } catch (error) { message.error(error?.response?.data?.message || 'No fue posible guardar el motivo') }
    finally { setSaving(false) }
  }
  const changeStatus = async (row) => {
    setSaving(true)
    try {
      await axios.patch(`${url}/${row.id}/status`, { active: !row.active })
      message.success('Estado actualizado')
      await load()
    } catch (error) { message.error(error?.response?.data?.message || 'No fue posible cambiar el estado') }
    finally { setSaving(false) }
  }
  if (!canView) return <Result status='403' title='Sin permisos para visualizar movimientos manuales' />
  const filtered = rows.filter(row => row.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
  return (
    <div className='implementation-maintenance'>
      <div className='implementation-maintenance__header'>
        <h2>Movimientos manuales</h2>
        <p>Administre los motivos disponibles al registrar un movimiento manual.</p>
      </div>
      <Card>
        <div className='implementation-maintenance__toolbar'>
          <Input.Search placeholder='Buscar motivo' allowClear value={search}
            onChange={event => { setSearch(event.target.value); setPage(1) }} style={{ maxWidth: 340 }} />
          <Space wrap>
            <Switch checked={includeInactive} onChange={value => { setIncludeInactive(value); setPage(1) }} /> Mostrar inactivos
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Actualizar</Button>
            {canCreate && <Button type='primary' icon={<PlusOutlined />} onClick={() => open()}>Nuevo motivo</Button>}
          </Space>
        </div>
        <Table rowKey='id' dataSource={filtered} loading={loading} scroll={{ x: 550 }}
          pagination={{ current: page, onChange: setPage, pageSize: 10, showSizeChanger: false }} columns={[
            { title: 'Motivo', dataIndex: 'name' },
            { title: 'Estado', dataIndex: 'active', render: active => <Tag color={active ? 'green' : 'default'}>{active ? 'Activo' : 'Inactivo'}</Tag> },
            { title: 'Acciones', render: (_, row) => canEdit && <Space>
              <Button type='link' icon={<EditOutlined />} onClick={() => open(row)}>Editar</Button>
              <Popconfirm title={row.active ? '¿Desactivar este motivo?' : '¿Activar este motivo?'} onConfirm={() => changeStatus(row)} okText='Confirmar' cancelText='Cancelar'>
                <Button type='link' disabled={saving}>{row.active ? 'Desactivar' : 'Activar'}</Button>
              </Popconfirm>
            </Space> }
          ]} />
      </Card>
      <Modal title={editor?.id ? 'Editar motivo' : 'Nuevo motivo'} open={editor !== null} onCancel={() => setEditor(null)} onOk={save} confirmLoading={saving} okText='Guardar' cancelText='Cancelar'>
        <Form form={form} layout='vertical'>
          <Form.Item label='Motivo' name='name' rules={[{ required: true, whitespace: true, message: 'Ingrese un motivo' }, { max: 150, message: 'Máximo 150 caracteres' }]}>
            <Input maxLength={150} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
